import { ApiClient } from '../ApiClient';

export class LPBondsNftsApi {
  /**
   * Constructs a new LPBondsNftsApi.
   * @alias module:api/LPBondsNftsApi
   * @class
   * @param {module:ApiClient} [apiClient] Optional API client implementation to use,
   * default to {@link module:ApiClient#lpBondsApiInstance} if unspecified.
   */
  constructor(apiClient) {
    this.apiClient = apiClient || ApiClient.lpBondsApiInstance;
  }

  /**
   * List all ERC721 &amp; ERC1155 that have been minted on chain
   * @param {Number} skip Numer of items to skip for the result set
   * @param {Number} limit Number of items to retrieve
   * @param {Number} buyNow Whether to fetch all nfts (0) or only which have been listed (1)
   */
  async list({ skip, limit, buyNow, owner }) {
    return await this.apiClient.callApi('/nfts', 'get', {}, { skip, limit, buyNow, owner });
  }

  /**
   * List all ERC721 &amp; ERC1155 from a contract address
   *
   * @param {Number} skip Numer of items to skip for the result set
   * @param {Number} limit Number of items to retrieve
   * @param {string} address Number of items to retrieve
   * @param {Number} buyNow Whether to fetch all nfts (0) or only which have been listed (1)
   */
  async getNftsFromAddress({ skip, limit, address, buyNow, tokenIds, traits, min, max }) {
    return await this.apiClient.callApi(
      '/nfts/{address}',
      'get',
      { address },
      { skip, limit, buyNow, tokenIds, ...traits, min, max },
    );
  }

  /**
   * Get NFT from tokenId and contract address
   *
   * @param {string} address Contract address of the token
   * @param {string} tokenId tokenId to retrieve data
   */
  async getNftTokenFromId({ contractAddress, tokenId }) {
    return await this.apiClient.callApi(
      '/nfts/{contractAddress}/{tokenId}',
      'get',
      { contractAddress, tokenId },
      {},
    );
  }

  /**
   * Get NFTs owned by a user in a specific collection
   *
   * @param {string} owner User wallet address
   * @param {string} contractAddress Contract address of the collection
   * @param {Number} skip Number of items to skip for the result set
   * @param {Number} limit Number of items to retrieve
   */
  async getNftsByOwnerInCollection({ owner, contractAddress, skip, limit }) {
    return await this.apiClient.callApi(
      '/nfts/from/{owner}/{contractAddress}',
      'get',
      { owner, contractAddress },
      { skip, limit },
    );
  }

  /**
   * Refresh metadata for specific NFT
   * @param {string} contractAddress Contract address of the nft
   * @param {string} tokenId tokenId of the nft
   */
  async refreshMetadata({ contractAddress, tokenId }) {
    return await this.apiClient.callApi(
      '/nfts/{contractAddress}/{tokenId}/metadata',
      'get',
      { contractAddress, tokenId },
      {},
      null,
    );
  }

  /**
   * Get trading history for specific NFT
   *
   * @param {string} contractAddress Contract address of the token
   * @param {string} tokenId Token ID
   * @param {boolean} listing Include listing events
   * @param {boolean} mint Include mint events
   * @param {boolean} sale Include sale events
   * @param {boolean} offer Include offer events
   * @param {boolean} bid Include bid events
   * @param {boolean} burn Include burn events
   * @param {boolean} transfer Include transfer events
   * @param {Number} skip Number of items to skip
   * @param {Number} limit Number of items to retrieve
   */
  async getTradingHistory({
    contractAddress,
    tokenId,
    listing,
    mint,
    sale,
    offer,
    bid,
    burn,
    transfer,
    skip,
    limit,
  }) {
    return await this.apiClient.callApi(
      '/nfts/tradingHistory/{contractAddress}/{tokenId}',
      'get',
      { contractAddress, tokenId },
      { listing, mint, sale, offer, bid, burn, transfer, skip, limit },
    );
  }

  /**
   * Get all bids for specific NFT by owner
   *
   * @param {string} ownerAddress Owner wallet address
   * @param {Array.<string>} contractAddresses Contract addresses to filter
   * @param {Number} limit Number of items to retrieve
   * @param {Array.<string>} exclude Addresses to exclude
   */
  async getNftBids({ ownerAddress, contractAddresses, limit, exclude }) {
    return await this.apiClient.callApi(
      '/nfts/bids/{ownerAddress}',
      'get',
      { ownerAddress },
      { contractAddresses, limit, exclude },
    );
  }

  /**
   * Get all offers for specific NFT
   *
   * @param {string} contractAddress Contract address of the token
   * @param {string} tokenId Token ID
   * @param {Number} skip Number of items to skip
   * @param {Number} limit Number of items to retrieve
   * @param {string} direction Sort direction (asc/desc)
   */
  async getAssetOffers({ contractAddress, tokenId, skip, limit, direction }) {
    return await this.apiClient.callApi(
      '/nfts/offers/{contractAddress}/{tokenId}',
      'get',
      { contractAddress, tokenId },
      { skip, limit, direction },
    );
  }
}
